import React from 'react';
import type { BoatAd, SearchKey } from '../../../types';
import { SearchAd } from '../SearchAd';
import { useSearchAdClasses } from '../SearchAdClasses';
import type { BaseSearchAdProps, SearchAdLayout } from '../SearchAdTypes';
import { BoatBullets } from './BoatBullets';
import { BoatHeading } from './BoatHeading';
import { BoatImage } from './BoatImage';
import { BoatLabel } from './BoatLabel';
import { BoatLocation } from './BoatLocation';
import { BoatOrganisation } from './BoatOrganisation';
import { BoatSummary } from './BoatSummary';

const gridClasses = {
    mobile: {
        ad: 's-bg',
        imageWrapper: '',
        image: '',
        location: 'mx-16 mt-8',
        content: 'm-16 mt-4',
        heading: 'break-words',
        summary: 'mt-16 flex justify-between',
        details: 'mt-4',
        ribbon: 'absolute top-0 left-0 pointer-events-none',
    },
    desktop: {
        summary: 'sm:mt-8',
        details: 'sm:mt-8',
    },
};

const listClasses = {
    mobile: {
        ad: 'grid grid-cols-3 grid-rows-2 gap-16 p-16 s-bg',
        imageWrapper: '',
        image: 'rounded-8 border s-border',
        location: 'col-span-2 pr-44',
        content: 'col-span-3',
        heading: 'break-words',
        summary: 'mt-16 flex justify-between',
        details: 'mt-4',
        ribbon: 'absolute top-0 left-0 pointer-events-none',
    },
    desktop: {
        ad: 'sm:gap-y-4',
        imageWrapper: 'sm:row-span-2',
        content: 'sm:col-span-2',
        heading: 'sm:pr-44',
        summary: 'sm:mt-8 sm:block',
    },
};

type BoatSearchAdProps = BaseSearchAdProps & {
    ad: BoatAd;
    imageSizes?: string;
    layout: SearchAdLayout;
    searchKey: SearchKey;
};

const listLayoutStyle: React.CSSProperties = {
    gridTemplateRows: 'min-content 1fr',
};

export function BoatSearchAd(props: BoatSearchAdProps) {
    const {
        ad,
        className = '',
        heading,
        image,
        imageSizes,
        label,
        layout,
        searchKey,
        ...searchAdProps
    } = props;

    const layoutClasses = layout === 'grid' ? gridClasses : listClasses;
    const classes = useSearchAdClasses({
        mode: searchAdProps.mode,
        mobileClasses: layoutClasses.mobile,
        desktopClasses: layoutClasses.desktop,
    });

    return (
        <SearchAd
            adId={ad.id}
            className={`${classes.ad} ${className}`}
            style={layout === 'list' ? listLayoutStyle : undefined}
            {...searchAdProps}
        >
            <div className={classes.imageWrapper}>
                <BoatImage
                    ad={ad}
                    image={image}
                    imageClass={classes.image}
                    sizes={imageSizes}
                />
            </div>
            <BoatLocation ad={ad} className={classes.location} />
            <div className={classes.content}>
                <BoatHeading
                    ad={ad}
                    className={classes.heading}
                    heading={heading}
                />
                <BoatSummary ad={ad} className={classes.summary} />
                <div className={classes.details}>
                    <BoatOrganisation ad={ad} searchKey={searchKey} />
                    <BoatBullets ad={ad} searchKey={searchKey} />
                </div>
            </div>
            <BoatLabel ad={ad} className={classes.ribbon} label={label} />
        </SearchAd>
    );
}
